import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { faCoins, faReceipt, faSackDollar } from "@fortawesome/free-solid-svg-icons"
import { useGetResellerCommissions, ResellerCommissionEntry } from "../../../hooks/Reseller/useGetResellerCommissions"
import { EResellerCommissionEntryStatus } from "../../../Enum/EResellerCommissionEntryStatus"
import { convertToLocalMoney, formatDateToString } from "../../../Objects/Generics/Helps"
import { FinancialDataTable } from "../../CashFlow/components/FinancialDataTable"
import { FinancialKpiCard } from "../../CashFlow/components/FinancialKpiCard"
import type { FinancialTableColumn } from "../../CashFlow/types/financialUi"

export const ResellerCommissionsSection: React.FC = () => {
  const { t } = useTranslation("reseller")

  const { handleGetResellerCommissions } = useGetResellerCommissions()

  const [entries, setEntries] = useState<ResellerCommissionEntry[] | null>(null)

  useEffect(() => {
    handleGetResellerCommissions(setEntries)
  }, [])

  const rows = entries ?? []

  const paidAmount = rows
    .filter((entry) => entry.status === EResellerCommissionEntryStatus.PaidOut)
    .reduce((sum, entry) => sum + entry.commissionAmount, 0)

  const pendingAmount = rows
    .filter((entry) => entry.status !== EResellerCommissionEntryStatus.PaidOut)
    .reduce((sum, entry) => sum + entry.commissionAmount, 0)

  const columns = useMemo<FinancialTableColumn<ResellerCommissionEntry>[]>(
    () => [
      {
        id: "company",
        label: t("commissions.column_company"),
        render: (entry) => <span className="font-extrabold text-slate-800">{entry.companyName || "—"}</span>,
        sortValue: (entry) => entry.companyName,
        mobilePriority: "primary",
      },
      {
        id: "payment",
        label: t("commissions.column_payment"),
        render: (entry) => convertToLocalMoney(entry.grossPaymentAmount),
        sortValue: (entry) => entry.grossPaymentAmount,
        align: "right",
        mobilePriority: "summary",
      },
      {
        id: "commission",
        label: t("commissions.column_commission"),
        render: (entry) => (
          <span className="font-extrabold text-orange-600">
            {convertToLocalMoney(entry.commissionAmount)} ({entry.commissionPercentageApplied}%)
          </span>
        ),
        sortValue: (entry) => entry.commissionAmount,
        align: "right",
        mobilePriority: "summary",
      },
      {
        id: "date",
        label: t("commissions.column_date"),
        render: (entry) => formatDateToString(entry.accruedAtUtc),
        sortValue: (entry) => new Date(entry.accruedAtUtc).getTime(),
      },
      {
        id: "status",
        label: t("commissions.column_status"),
        render: (entry) => (
          <span
            className={`rounded-full px-3 py-1 text-xs font-bold ${
              entry.status === EResellerCommissionEntryStatus.PaidOut
                ? "bg-emerald-50 text-emerald-700"
                : "bg-amber-50 text-amber-700"
            }`}
          >
            {entry.status === EResellerCommissionEntryStatus.PaidOut
              ? t("commissions.status_paid")
              : t("commissions.status_accrued")}
          </span>
        ),
        sortValue: (entry) => entry.status,
      },
    ],
    [t],
  )

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <FinancialKpiCard title={t("commissions.title")} value={`${rows.length}`} icon={faReceipt} tone="brand" />

        <FinancialKpiCard
          title={t("overview.commission_paid")}
          value={convertToLocalMoney(paidAmount)}
          icon={faSackDollar}
          tone="positive"
        />

        <FinancialKpiCard
          title={t("overview.commission_pending")}
          value={convertToLocalMoney(pendingAmount)}
          icon={faCoins}
          tone="warning"
        />
      </div>

      <FinancialDataTable
        title={t("commissions.title")}
        rows={rows}
        getRowKey={(entry) => entry.id}
        columns={columns}
        searchText={(entry) => `${entry.companyName} ${formatDateToString(entry.accruedAtUtc)}`}
        searchLabel={t("commissions.search")}
        emptyTitle={t("commissions.empty")}
        icon={faReceipt}
        paginationLabels={{
          ariaLabel: t("commissions.title"),
          previous: t("table.previous"),
          next: t("table.next"),
          page: (current, total) => t("table.page", { current, total }),
          range: (first, last, total) => t("table.range", { first, last, total }),
          itemsPerPage: t("table.items_per_page"),
        }}
      />
    </div>
  )
}
